/**
 * Ranking Card Shared Module
 * Shared rendering, stats computation, sub-collection fetch, and utilities
 * used by both latest-rankings and products-ranking-grid sections.
 */

class RankingCardRenderer {
  constructor(settings) {
    this.settings = settings;
  }

  // === Sub-collection fetch ===

  async fetchSubCollectionProducts(shopDomain, token, handle) {
    const allProducts = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const result = await this.fetchSubCollectionPage(shopDomain, token, handle, cursor);
      allProducts.push(...result.products);
      hasNextPage = result.hasNextPage;
      cursor = result.endCursor;
    }

    return allProducts;
  }

  async fetchSubCollectionPage(shopDomain, token, handle, cursor = null) {
    const afterClause = cursor ? `, after: "${cursor}"` : '';

    const query = `
      query GetCompanyProducts($handle: String!) {
        collection(handle: $handle) {
          products(first: 100${afterClause}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                overall_score: metafield(namespace: "custom", key: "overall_score") {
                  value
                }
                precio_actual: metafield(namespace: "custom", key: "precio_actual") {
                  value
                }
                precio_base_6m: metafield(namespace: "custom", key: "precio_base_6m") {
                  value
                }
                precio_base_12m: metafield(namespace: "custom", key: "precio_base_12m") {
                  value
                }
                precio_base_24m: metafield(namespace: "custom", key: "precio_base_24m") {
                  value
                }
                fundamentos_score: metafield(namespace: "custom", key: "fundamentos_score") {
                  value
                }
                crecimiento_score: metafield(namespace: "custom", key: "crecimiento_score") {
                  value
                }
                rentabilidad_score: metafield(namespace: "custom", key: "rentabilidad_score") {
                  value
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query,
        variables: { handle }
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    const collection = data.data?.collection;
    if (!collection) {
      return { products: [], hasNextPage: false, endCursor: null };
    }

    const products = collection.products.edges.map(edge => edge.node);

    return {
      products,
      hasNextPage: collection.products.pageInfo.hasNextPage,
      endCursor: collection.products.pageInfo.endCursor
    };
  }

  // === Stats computation ===

  computeRankingStats(companyProducts) {
    const scores = [];
    const vars6m = [];
    const vars12m = [];
    const vars24m = [];
    const fundamentosScores = [];
    const crecimientoScores = [];
    const rentabilidadScores = [];

    for (const node of companyProducts) {
      const score = parseInt(node.overall_score?.value);
      if (!isNaN(score) && score > 0) scores.push(score);

      const fund = parseInt(node.fundamentos_score?.value);
      if (!isNaN(fund) && fund > 0) fundamentosScores.push(fund);
      const crec = parseInt(node.crecimiento_score?.value);
      if (!isNaN(crec) && crec > 0) crecimientoScores.push(crec);
      const rent = parseInt(node.rentabilidad_score?.value);
      if (!isNaN(rent) && rent > 0) rentabilidadScores.push(rent);

      const actual = this.parsePrice(node.precio_actual?.value);
      if (actual.value === null || actual.value <= 0) continue;

      const p6m = this.parsePrice(node.precio_base_6m?.value);
      const p12m = this.parsePrice(node.precio_base_12m?.value);
      const p24m = this.parsePrice(node.precio_base_24m?.value);

      if (p6m.value !== null && p6m.value > 0) {
        vars6m.push(((p6m.value - actual.value) / actual.value) * 100);
      }
      if (p12m.value !== null && p12m.value > 0) {
        vars12m.push(((p12m.value - actual.value) / actual.value) * 100);
      }
      if (p24m.value !== null && p24m.value > 0) {
        vars24m.push(((p24m.value - actual.value) / actual.value) * 100);
      }
    }

    const avg = (arr) => arr.length === 0 ? null : arr.reduce((a, b) => a + b, 0) / arr.length;

    const topNAvg = (arr, n) => {
      if (arr.length === 0) return null;
      const sorted = [...arr].sort((a, b) => b - a);
      const top = sorted.slice(0, n);
      return top.reduce((a, b) => a + b, 0) / top.length;
    };

    const bottomNAvg = (arr, n) => {
      if (arr.length === 0) return null;
      const sorted = [...arr].sort((a, b) => a - b);
      const bottom = sorted.slice(0, n);
      return bottom.reduce((a, b) => a + b, 0) / bottom.length;
    };

    const countPositive = (arr) => arr.filter(v => v > 0).length;
    const countNegative = (arr) => arr.filter(v => v < 0).length;

    return {
      avgScore: scores.length > 0 ? Math.round(avg(scores)) : null,
      avgVar6m: avg(vars6m),
      avgVar12m: avg(vars12m),
      avgVar24m: avg(vars24m),
      top3Var6m: topNAvg(vars6m, 3),
      top3Var12m: topNAvg(vars12m, 3),
      top3Var24m: topNAvg(vars24m, 3),
      bot3Var6m: bottomNAvg(vars6m, 3),
      bot3Var12m: bottomNAvg(vars12m, 3),
      bot3Var24m: bottomNAvg(vars24m, 3),
      countUp6m: countPositive(vars6m),
      countDown6m: countNegative(vars6m),
      countUp12m: countPositive(vars12m),
      countDown12m: countNegative(vars12m),
      countUp24m: countPositive(vars24m),
      countDown24m: countNegative(vars24m),
      avgFundamentos: fundamentosScores.length > 0 ? Math.round(avg(fundamentosScores)) : null,
      avgCrecimiento: crecimientoScores.length > 0 ? Math.round(avg(crecimientoScores)) : null,
      avgRentabilidad: rentabilidadScores.length > 0 ? Math.round(avg(rentabilidadScores)) : null
    };
  }

  // === Render ===

  createCardElement(ranking) {
    const article = document.createElement('article');
    article.className = 'latest-rankings__card';
    article.setAttribute('data-card-id', ranking.id);

    const settings = this.settings;
    const buttonArrowClass = settings.buttonArrow ? 'button--arrow' : '';

    // Meta line: num empresas + fecha
    const metaParts = [];
    if (ranking.productsCount > 0) {
      metaParts.push(`${ranking.productsCount} ${settings.companiesLabel}`);
    }
    if (ranking.report_date) {
      metaParts.push(this.formatDate(ranking.report_date));
    }
    const metaHTML = metaParts.length > 0
      ? `<p class="latest-rankings__meta">${this.escapeHTML(metaParts.join(' · '))}</p>`
      : '';

    // Description
    const descText = this.truncateText(ranking.description, settings.descriptionLimit);
    const descHTML = descText
      ? `<p class="latest-rankings__description">${this.escapeHTML(descText)}</p>`
      : '';

    // Score bar
    const scoreHTML = this.renderScoreBar(ranking.stats);

    // Horizon grid
    const horizonHTML = this.renderHorizonGrid(ranking.stats);

    // Top 3
    const top3HTML = this.renderTop3Row(ranking.stats);

    // Price
    let priceHTML = '';
    if (settings.showPrice !== false && ranking.priceAmount !== null && ranking.priceAmount > 0) {
      priceHTML = `
        <div class="latest-rankings__price-wrapper">
          <span class="latest-rankings__price">${this.formatCurrency(ranking.priceAmount, ranking.currencyCode)}</span>
          <span class="latest-rankings__price-label">(${this.escapeHTML(settings.priceLabel)})</span>
        </div>
      `;
    }

    article.innerHTML = `
      <div class="latest-rankings__content">
        <div class="latest-rankings__header">
          <h3 class="latest-rankings__title">
            <a href="${ranking.url}">${this.escapeHTML(ranking.title)}</a>
          </h3>
          ${metaHTML}
        </div>

        ${descHTML}
        ${scoreHTML}
        ${horizonHTML}
        ${top3HTML}

        <div class="latest-rankings__footer">
          ${priceHTML}
          <div class="latest-rankings__cta">
            <a href="${ranking.url}" class="button button--${settings.buttonStyle} button--small ${buttonArrowClass}">
              ${this.escapeHTML(settings.cardButtonLabel)}
            </a>
          </div>
        </div>
      </div>
    `;

    return article;
  }

  renderScoreBar(stats) {
    if (!stats || stats.avgScore === null) return '';

    const pct = Math.min(100, Math.max(0, stats.avgScore));

    return `
      <div class="latest-rankings__score-section">
        <div class="latest-rankings__score-header">
          <span class="latest-rankings__score-value">${stats.avgScore}</span>
          <span class="latest-rankings__score-label">${this.escapeHTML(this.settings.scoreLabel)}</span>
        </div>
        <div class="latest-rankings__score-bar">
          <div class="latest-rankings__score-fill" style="width: ${pct}%"></div>
        </div>
      </div>
    `;
  }

  renderHorizonGrid(stats) {
    if (!stats) return '';

    const hasAnyVar = stats.avgVar6m !== null || stats.avgVar12m !== null || stats.avgVar24m !== null;
    if (!hasAnyVar) return '';

    const renderCell = (label, avgVar, countUp, countDown) => {
      if (avgVar === null) {
        return `
          <div class="latest-rankings__horizon-cell">
            <span class="latest-rankings__horizon-label">${label}</span>
            <span class="latest-rankings__horizon-value">—</span>
          </div>
        `;
      }

      const sign = avgVar >= 0 ? '+' : '';
      const colorClass = avgVar >= 0 ? 'latest-rankings__horizon-value--positive' : 'latest-rankings__horizon-value--negative';
      const total = countUp + countDown;

      let countsHTML = '';
      if (total > 0) {
        const upPct = Math.round((countUp / total) * 100);
        countsHTML = `
          <div class="latest-rankings__ratio">
            <div class="latest-rankings__ratio-bar">
              <div class="latest-rankings__ratio-fill--up" style="width: ${upPct}%"></div>
              <div class="latest-rankings__ratio-fill--down" style="width: ${100 - upPct}%"></div>
            </div>
            <span class="latest-rankings__ratio-text">
              <span class="latest-rankings__count-up">${countUp} ${this.escapeHTML(this.settings.upLabel)}</span>
              <span class="latest-rankings__ratio-sep">&middot;</span>
              <span class="latest-rankings__count-down">${countDown} ${this.escapeHTML(this.settings.downLabel)}</span>
            </span>
          </div>
        `;
      }

      return `
        <div class="latest-rankings__horizon-cell">
          <span class="latest-rankings__horizon-label">${label}</span>
          <span class="latest-rankings__horizon-value ${colorClass}">${sign}${avgVar.toFixed(1)}%</span>
          ${countsHTML}
        </div>
      `;
    };

    return `
      <div class="latest-rankings__horizon-section">
        <span class="latest-rankings__horizon-title">${this.escapeHTML(this.settings.projectionsLabel)}</span>
        <div class="latest-rankings__horizon-grid">
          ${renderCell('6M', stats.avgVar6m, stats.countUp6m, stats.countDown6m)}
          ${renderCell('12M', stats.avgVar12m, stats.countUp12m, stats.countDown12m)}
          ${renderCell('24M', stats.avgVar24m, stats.countUp24m, stats.countDown24m)}
        </div>
      </div>
    `;
  }

  renderTop3Row(stats) {
    if (!stats) return '';

    const hasAny = stats.top3Var6m !== null || stats.top3Var12m !== null || stats.top3Var24m !== null;
    if (!hasAny) return '';

    const renderCell = (label, val) => {
      if (val === null) {
        return `
          <div class="latest-rankings__horizon-cell">
            <span class="latest-rankings__horizon-label">${label}</span>
            <span class="latest-rankings__horizon-value">—</span>
          </div>
        `;
      }

      const sign = val >= 0 ? '+' : '';
      const colorClass = val >= 0 ? 'latest-rankings__horizon-value--positive' : 'latest-rankings__horizon-value--negative';

      return `
        <div class="latest-rankings__horizon-cell">
          <span class="latest-rankings__horizon-label">${label}</span>
          <span class="latest-rankings__horizon-value ${colorClass}">${sign}${val.toFixed(1)}%</span>
        </div>
      `;
    };

    return `
      <div class="latest-rankings__horizon-section latest-rankings__top3-section">
        <span class="latest-rankings__horizon-title">${this.escapeHTML(this.settings.top3Label)}</span>
        <div class="latest-rankings__horizon-grid">
          ${renderCell('6M', stats.top3Var6m)}
          ${renderCell('12M', stats.top3Var12m)}
          ${renderCell('24M', stats.top3Var24m)}
        </div>
      </div>
    `;
  }

  // === Animation ===

  setupCardObservers(cards, container) {
    const observers = [];
    const options = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.2
    };

    cards.forEach((card) => {
      const scoreFill = card.querySelector('.latest-rankings__score-fill');

      if (scoreFill) {
        scoreFill.style.animationPlayState = 'paused';
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCardElements(card);
            observer.unobserve(entry.target);
          }
        });
      }, options);

      observer.observe(card);
      observers.push(observer);
    });

    return observers;
  }

  animateCardElements(card) {
    if (card.hasAttribute('data-animated')) return;
    card.setAttribute('data-animated', 'true');

    const scoreFill = card.querySelector('.latest-rankings__score-fill');
    if (scoreFill) {
      scoreFill.style.animationPlayState = 'running';
    }
  }

  // === Utils ===

  parsePrice(raw) {
    if (!raw) return { value: null, currency: '' };

    let currency = '';
    if (raw.includes('HK$')) currency = 'HK$';
    else if (raw.includes('€') || raw.includes('&#8364;')) currency = '€';
    else if (raw.includes('£')) currency = '£';
    else if (raw.includes('$')) currency = '$';

    const cleaned = raw
      .replace(/HK\$/g, '')
      .replace(/&#8364;/g, '')
      .replace(/[€$£]/g, '')
      .replace(/\s/g, '')
      .replace(/,/g, '');

    const value = parseFloat(cleaned);
    return { value: isNaN(value) ? null : value, currency };
  }

  formatCurrency(amount, currencyCode) {
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: currencyCode || 'EUR'
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} €`;
    }
  }

  formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  }

  escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  showError(gridElement, message) {
    gridElement.innerHTML = `
      <div class="latest-rankings__error">
        <p>${message}</p>
      </div>
    `;
  }

  showEmpty(gridElement, title, text) {
    gridElement.innerHTML = `
      <div class="latest-rankings__empty">
        <h3>${this.escapeHTML(title)}</h3>
        <p>${this.escapeHTML(text)}</p>
      </div>
    `;
  }
}

window.RankingCardRenderer = RankingCardRenderer;
